import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import "../styles/BlogPost.css";
import "../styles/Blog4.css";

function Blog11() {
  return (
    <>
      <Navbar />

      <article className="blog-post">

        {/* ── Breadcrumb ── */}
        <p className="blog-breadcrumb">
          <span>Technology</span>
          <span className="breadcrumb-divider">|</span>
          <span className="breadcrumb-section">Software Development</span>
        </p>

        {/* ── Headline & dek ── */}
        <h1 className="blog-headline">
          Why I'm Filming How Coding Is Changing (And Why You Should Care)
        </h1>

        <p className="blog-dek">
          Recording the shift to AI-assisted development in real time — and what it means for how we build and learn to code
        </p>

        {/* ── Video embed (top of article) ── */}
        <div
          style={{
            position: "relative",
            width: "100%",
            maxWidth: "560px",
            aspectRatio: "16 / 9",
            margin: "0 auto 24px",
          }}
        >
          <iframe
            src="https://www.youtube.com/embed/BlJpJ4bVhZo?si=Qryf3ZALrwNXxRYF"
            title="YouTube video player"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              border: 0,
            }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          ></iframe>
        </div>

        {/* ── Share row ── */}
        <div className="blog-meta-row">
          <button
            className="blog-share-btn"
            onClick={() =>
              navigator.share?.({ title: document.title, url: window.location.href })
            }
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
            </svg>
            Share
          </button>
        </div>

        {/* ── Date + read time ── */}
        <p className="blog-dateline">August 24th 2026 &nbsp;|&nbsp; 5 min read</p>

        {/* ── Body copy ── */}
        <div className="blog-body">

          <p className="blog-drop-cap">
            A few days ago I started recording short videos showing, in
            real time, how I am building a website (partially with the
            help of AI). Not polished tutorials, just quick clips of
            prompts going in and working code coming out. The ground under
            "how you learn to code" and "how you build software" has
            shifted faster than almost anyone expected.
          </p>

          <h2 className="blog-section-heading">
            The shift isn't coming — it already happened
          </h2>

          <p>
            It's tempting to treat AI-assisted coding as a trend still
            finding its footing. The numbers say otherwise. The Stack
            Overflow Developer Survey 2025 found that 84% of developers
            were using or planning to use AI tools in their development
            process, and that 51% of professional developers were already
            using AI tools every single workday. JetBrains' State of
            Developer Ecosystem survey tells a similar story from a
            different angle: regular AI tool usage jumped from 62% of
            developers in 2025 to 85% in 2026, a leap the report frames as
            the tools moving from optional add-on to standard
            infrastructure.
          </p>

          <p>
            What's striking isn't just that people are trying these tools,
            it's who's starting there. GitHub's own platform data shows
            that 80% of new developers on GitHub now use Copilot within
            their very first week on the platform. For an entire
            generation of people learning to code, AI assistance isn't an
            advanced technique layered on top of "real" programming. It's
            simply what programming looks like from day one.
          </p>

          <h2 className="blog-section-heading">
            What's actually different about how we build
          </h2>

          <p>
            The change isn't just "AI helps you type faster." It's a
            genuine shift in workflow and mindset. A few patterns show up
            over and over in the tools and communities I follow for the
            videos:
          </p>

          <h3 className="blog-subsection-heading">
            Conversational building, not line-by-line typing
          </h3>

          <p>
            Instead of writing every function by hand, you describe what
            you want (for example: "a booking page for my salon with
            online payments") and an AI agent generates working code. You
            then steer through follow-up prompts. Industry writers have
            taken to calling this "vibe coding," a term that's become
            genuinely divisive: some treat it as shorthand for reckless,
            unreviewed code generation, while a more disciplined version
            keeps a human accountable for the parts that matter and treats
            the AI output as a fast first draft, not a finished product.
          </p>

          <h3 className="blog-subsection-heading">
            Agents that work across a whole codebase
          </h3>

          <p>
            The newest wave of tools doesn't just autocomplete a line, it
            can read an entire project, make multi-file changes, run
            tests, and fix its own mistakes. That's a meaningfully
            different job than the AI code-completion tools from just a
            couple of years ago.
          </p>

          <h3 className="blog-subsection-heading">
            A widening gap between "can generate" and "should ship"
          </h3>

          <p>
            This part matters, and it's worth saying plainly in a video
            series aimed at beginners as much as pros: faster code
            generation hasn't automatically meant better code. Developer
            trust in AI-generated output has actually declined even as
            usage has climbed, and independent research has surfaced real
            nuance behind the productivity story. One controlled study of
            experienced developers found they were measurably slower using
            an AI assistant on their own complex codebases, even though
            they perceived themselves as faster. The lesson isn't "don't
            use AI." It's that reviewing, testing, and understanding the
            code you ship still matters, more than ever, since more and
            more of what ends up in production was never typed by a human
            hand.
          </p>

          <h2 className="blog-section-heading">
            You really can build a website for free right now
          </h2>

          <p>
            Here's the part that still amazes me every time I say it out
            loud in a video: with tools available today, you can build and
            host a working website for free, with zero prior coding
            experience. Prompt-driven builders can take a plain-language
            description and turn it into a live, working site or app in
            minutes. A teenager can describe an app idea out loud and have
            something to show a real customer within days, not months.
          </p>

          <p>
            That doesn't mean the craft of software engineering has
            disappeared. In fact, in my opinion, we are far from it.
            Someone still has to make good decisions about security,
            structure, and what happens when the free demo needs to become
            a real, reliable product. But the starting cost of turning an
            idea into something clickable has collapsed. That's the story
            I keep trying to capture on camera: not "AI writes your app
            for you" so much as "the distance between an idea and a
            working prototype has gotten dramatically shorter."
          </p>

          <h2 className="blog-section-heading">
            The tools — and the way we learn — won't sit still
          </h2>

          <p>
            If there's one thing that's become clear from following this
            space closely enough to make videos about it, it's that there
            is no finish line here. The specific tool that's best this
            month will likely be replaced or outclassed within a year. The
            workflow I filmed yesterday will already look dated six months
            from now. What doesn't change is the underlying skill that
            matters most going forward: knowing how to direct, question,
            and verify what an AI produces, rather than either blindly
            trusting it or refusing to touch it.
          </p>

          <p>
            That's also why I think the best way to learn any of this
            right now is to watch someone actually do it. That's how you
            see the mistakes, dead ends, and iterations. Coding education
            itself is going through the same transformation as coding
            practice: less "memorize the syntax," more "learn to have a
            good conversation with the machine and know when to push back
            on what it gives you."
          </p>

          <p>
            The tools will keep changing. The methodologies will keep
            changing. That's exactly why I'm filming as I go, instead of
            waiting for things to settle down. Because at this rate, I
            don't see things "settling."
          </p>

          <p className="blog-sources">
            Sources: Stack Overflow Developer Survey 2025; JetBrains State
            of Developer Ecosystem Survey 2025–2026; GitHub Octoverse 2025;
            METR controlled study on AI coding assistant productivity
            (2025).
          </p>

        </div>
      </article>

      <Footer />
    </>
  );
}

export default Blog11;